/**
 * PreferenceCenter — Floating privacy button & re-consent panel.
 *
 * Renders a persistent floating icon after initial consent is given.
 * Clicking it lets users update their preferences at any time.
 */

import type { BannerConfig } from "./types";
import type { ConsentDecisions } from "../core/StorageAdapter";
import type { GeoResult } from "../core/GeoDetector";
import type { VendorList } from "../tcf/TcfTypes";
import { BannerRenderer, sanitizeCssValue, escapeHtmlValue, type ConsentMeta } from "./BannerRenderer";
import { resolveTranslation, RTL_LANGUAGES } from "../i18n/translations";

type UpdateCallback = (decisions: ConsentDecisions) => void;

export class PreferenceCenter {
  private host: HTMLElement | null = null;
  private triggerEl: HTMLElement | null = null;
  private activeRenderer: BannerRenderer | null = null;
  private config: BannerConfig;
  private geo: GeoResult;
  private getCurrentDecisions: () => ConsentDecisions;
  private onUpdate: UpdateCallback;
  private lang: string;
  private vendorList: VendorList | null;
  private getConsentMeta: () => ConsentMeta | null;

  constructor(
    config: BannerConfig,
    geo: GeoResult,
    getCurrentDecisions: () => ConsentDecisions,
    onUpdate: UpdateCallback,
    lang: string = "en",
    vendorList: VendorList | null = null,
    getConsentMeta: () => ConsentMeta | null = () => null
  ) {
    this.config = config;
    this.geo = geo;
    this.getCurrentDecisions = getCurrentDecisions;
    this.onUpdate = onUpdate;
    this.lang = lang;
    this.vendorList = vendorList;
    this.getConsentMeta = getConsentMeta;
  }

  show(): void {
    // A detached host does not count as shown: an SPA router that replaces
    // document.body's children takes ours with it, and returning early on the
    // stale reference would strip the site of its withdraw-consent affordance
    // for the rest of the page's life.
    if (this.host?.isConnected) return;
    this.host = null;
    this.triggerEl = null;

    if (!document.body) {
      document.addEventListener("DOMContentLoaded", () => this.show(), { once: true });
      return;
    }

    this.host = document.createElement("div");
    this.host.id = "pb-preference-host";
    this.host.style.cssText = "position: fixed; z-index: 2147483646; font-size: 16px;";
    document.body.appendChild(this.host);

    // Open, for the same reason as BannerRenderer: customers audit this with
    // tooling that reaches shadow content through host.shadowRoot.
    const shadow = this.host.attachShadow({ mode: "open" });
    const { config } = this;
    const customCopy = config.custom_copy as Record<string, Record<string, string>> | null;
    const privacyLabel = escapeHtmlValue(resolveTranslation("privacySettings", this.lang, customCopy));
    const isRtl = RTL_LANGUAGES.has(this.lang);
    // Sanitize tenant colours before they enter the <style> block.
    const bg = sanitizeCssValue(config.background_color);
    const primary = sanitizeCssValue(config.primary_color);
    const text = sanitizeCssValue(config.text_color);

    shadow.innerHTML = `
      <style>
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .pb-trigger {
          position: fixed;
          bottom: 20px;
          inset-inline-start: 20px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: ${bg};
          border: 1.5px solid ${primary}40;
          box-shadow: 0 2px 12px rgba(0,0,0,0.15);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .pb-trigger:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 16px rgba(0,0,0,0.2);
        }
        .pb-trigger:focus-visible {
          outline: 2px solid ${primary};
          outline-offset: 2px;
        }
        .pb-icon { width: 22px; height: 22px; }
        .pb-tooltip {
          position: fixed;
          bottom: 64px;
          inset-inline-start: 12px;
          background: ${bg};
          color: ${text};
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 11px;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 6px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.12);
          border: 1px solid ${text}15;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.15s;
        }
        .pb-trigger:hover + .pb-tooltip { opacity: 1; }
      </style>
      <button class="pb-trigger" id="pb-trigger" aria-label="${privacyLabel}" aria-haspopup="dialog"${isRtl ? ' style="direction:rtl"' : ''}>
        <svg class="pb-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="pb-grad-shield" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#29ABE2"/>
              <stop offset="100%" stop-color="#7B2FBE"/>
            </linearGradient>
            <linearGradient id="pb-grad-p" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#4B6FE4"/>
              <stop offset="100%" stop-color="#7B2FBE"/>
            </linearGradient>
          </defs>
          <path fill="url(#pb-grad-shield)" d="M50 4 L10 18 L10 50 C10 72 28 90 50 96 C72 90 90 72 90 50 L90 18 Z"/>
          <path fill="white" d="M38 28 L38 72 L46 72 L46 58 L54 58 C63 58 70 52 70 43 C70 34 63 28 54 28 Z M46 36 L54 36 C58 36 62 39 62 43 C62 47 58 50 54 50 L46 50 Z"/>
        </svg>
      </button>
      <div class="pb-tooltip">${privacyLabel}</div>
    `;

    this.triggerEl = shadow.getElementById("pb-trigger");
    this.triggerEl?.addEventListener("click", () => this.openPreferences());
  }

  hide(): void {
    this.host?.remove();
    this.host = null;
    this.triggerEl = null;
  }

  openPreferences(): void {
    if (this.activeRenderer) return; // Panel already open — never stack a second one
    if (document.getElementById("pb-consent-host")) return; // A banner is already on screen
    const current = this.getCurrentDecisions();
    const meta = this.getConsentMeta();
    // The panel renders in its own shadow host, so returning focus to the
    // trigger when it closes is ours to do.
    const done = () => {
      this.activeRenderer = null;
      this.triggerEl?.focus();
    };
    this.activeRenderer = new BannerRenderer(
      this.config,
      this.geo,
      (decisions) => {
        this.onUpdate(decisions);
        done();
      },
      this.lang,
      current,
      this.vendorList,
      meta,
      // Dismiss (Escape/close/overlay) just closes the panel; the stored
      // consent decision is preserved untouched.
      done
    );
    this.activeRenderer.show();
  }
}
